import { useState, useEffect, useCallback } from 'react';
import { LATEST_PACKAGE_FOLDER, latestPackage, type PackageDetails } from '../services/Clients/S3Client';
import { PackageTree, type TreeSelection } from '../services/Clients/PackageTree';

export const useS3Selection = (
  packageTree: PackageTree,
  currentBranch: string,
  currentVersion: string
) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [selectedBranch, setSelectedBranch] = useState<PackageDetails | null>(null);
  const [selectedPackage, setSelectedPackage] = useState<PackageDetails | null>(null);
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [retryAttempt, setRetryAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setIsLoaded(false);
      setLoadError(null);

      try {
        const requiredSelection: TreeSelection = {
          branch: currentBranch,
          version: currentVersion,
        };

        await packageTree.fetchTreeInformation(requiredSelection);

        if (cancelled) {
          return;
        }

        const initialBranch = packageTree.getBranchByFolder(currentBranch);
        if (!initialBranch) {
          throw new Error(`The selected branch "${currentBranch}" was not found.`);
        }

        const initialVersion = packageTree.getVersionByBranch(initialBranch, currentVersion);
        setSelectedBranch(initialBranch);
        setSelectedPackage(initialVersion ?? latestPackage());

        if (!cancelled) {
          setIsLoaded(true);
        }
      } catch (error) {
        if (!cancelled) {
          setLoadError(error instanceof Error ? error : new Error('Unable to load S3 selections.'));
          setIsLoaded(true);
        }
      }
    };

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, [packageTree, currentBranch, currentVersion, retryAttempt]);

  const retry = useCallback(() => {
    setRetryAttempt(attempt => attempt + 1);
  }, []);

  const selectBranch = useCallback((branchFolder: string) => {
    if (selectedBranch?.folder !== branchFolder) {
      const newSelectedBranch = packageTree.getBranchByFolder(branchFolder)!;
      setSelectedBranch(newSelectedBranch);
      // Reset package to latest when branch changes
      setSelectedPackage(latestPackage()); 
    }
  }, [packageTree, selectedBranch]);

  const selectPackage = useCallback((packageFolder: string) => {
    if (selectedBranch) {
      const latestPkg = latestPackage();
      const newSelectedVersion = packageFolder === LATEST_PACKAGE_FOLDER
        ? latestPkg
        : packageTree.getVersionByBranch(selectedBranch, packageFolder)!;
      setSelectedPackage(newSelectedVersion);
    }
  }, [packageTree, selectedBranch]);

  return {
    isLoaded,
    loadError,
    retry,
    selectedBranch,
    selectedPackage,
    selectBranch,
    selectPackage,
  };
};
